import React, { useState } from 'react'
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Alert, 
  InputAdornment,
  CircularProgress,
  Link
} from '@mui/material'
import { useAuth } from '../../contexts/AuthContext'
import PhoneIcon from '@mui/icons-material/Phone'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'

const PhoneAuth = ({ onSuccess, switchToEmail }) => {
  const { initiatePhoneAuth, verifyPhoneOtp, loading, error: authError, phoneVerificationInProgress, cancelPhoneVerification } = useAuth()
  const [phoneNumber, setPhoneNumber] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [error, setError] = useState(null)
  
  // Clean phone number (remove spaces, dashes, etc)
  const cleanPhoneNumber = (number) => {
    return number.replace(/[^\d+]/g, '')
  }

  // Validate Swedish phone number
  const validatePhoneNumber = (number) => {
    const cleanedNumber = cleanPhoneNumber(number)
    
    // Check if it's already in E.164 format (starting with +46 for Sweden)
    const e164Regex = /^\+46[1-9]\d{7,9}$/
    if (e164Regex.test(cleanedNumber)) {
      return true
    }
    
    // Check if it's a Swedish mobile number without country code
    // Swedish mobile numbers typically start with 07X
    const swedishMobileRegex = /^0(7[02369])\d{7,8}$/
    return swedishMobileRegex.test(cleanedNumber)
  }
  
  // Format to E.164 format
  const formatToE164 = (number) => {
    const cleanedNumber = cleanPhoneNumber(number)
    
    // If already in E.164 format, return as is
    if (cleanedNumber.startsWith('+')) {
      return cleanedNumber
    }
    
    // If it's a Swedish mobile number starting with 0, convert to +46
    if (cleanedNumber.startsWith('0')) {
      return '+46' + cleanedNumber.substring(1)
    }
    
    // If no conversion rule applies, add +46 prefix
    return '+46' + cleanedNumber
  }

  const handlePhoneSubmit = async (e) => {
    e.preventDefault()
    
    const cleanedNumber = cleanPhoneNumber(phoneNumber)
    
    if (!cleanedNumber) {
      setError('Mobilnummer får inte vara tomt')
      return
    }
    
    if (!validatePhoneNumber(cleanedNumber)) {
      setError('Ange ett giltigt svenskt mobilnummer (t.ex. 070-123 45 67)')
      return
    }

    // Format the phone number to E.164 before sending
    const formattedNumber = formatToE164(cleanedNumber)
    
    // Reset error before attempting to send OTP
    setError(null)
    
    try {
      const { error } = await initiatePhoneAuth(formattedNumber)
      
      if (error) {
        setError(error.message)
      }
    } catch (err) {
      console.error('Error initiating phone auth:', err)
      setError('Ett fel uppstod vid skickande av verifieringskod. Försök igen.')
    }
  }
  
  const handleOtpSubmit = async (e) => {
    e.preventDefault()
    
    if (!otpCode || otpCode.length < 6) {
      setError('Vänligen ange den 6-siffriga koden')
      return
    }
    
    // Reset error before attempting to verify OTP
    setError(null)
    
    const { error } = await verifyPhoneOtp(otpCode)
    
    if (error) {
      setError(error.message)
    } else {
      if (onSuccess) onSuccess()
    }
  }
  
  const handleCancelVerification = () => {
    cancelPhoneVerification()
    setOtpCode('')
  }
  
  // Render phone number input form
  const renderPhoneForm = () => (
    <Box component="form" onSubmit={handlePhoneSubmit}>
      <Typography 
        variant="h5" 
        component="h2" 
        gutterBottom 
        fontWeight="600"
        sx={{ mb: 2 }}
      >
        Logga in eller skapa konto
      </Typography>
      
      <Typography 
        variant="body1" 
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Ange ditt mobilnummer för logga in eller skapa nytt konto.
      </Typography>
      
      {(error || authError) && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            '& .MuiAlert-icon': { alignItems: 'center' }
          }}
        >
          {error || authError}
        </Alert>
      )}
      
      <TextField
        label="Mobilnummer"
        type="tel"
        fullWidth
        margin="normal"
        required
        placeholder="070-123 45 67"
        value={phoneNumber}
        onChange={(e) => setPhoneNumber(e.target.value)}
        autoComplete="tel"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PhoneIcon color="action" fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />
      
      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        sx={{ 
          mb: 2,
          height: 48
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Fortsätt'}
      </Button>
      
      <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mb: 3 }}>
        Genom att fortsätta godkänner du vår{' '}
        <Link 
          href="#" 
          onClick={(e) => {
            e.preventDefault()
            const event = new CustomEvent('openPrivacyPolicy')
            window.dispatchEvent(event)
          }}
          sx={{ color: theme => theme.palette.primary.main }}
        >
          integritetspolicy
        </Link>
      </Typography>
    </Box>
  )
  
  // Render OTP verification form
  const renderOtpForm = () => (
    <Box component="form" onSubmit={handleOtpSubmit}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={handleCancelVerification}
          sx={{ mr: 2 }}
        >
          Tillbaka
        </Button>
        <Typography 
          variant="h5" 
          component="h2" 
          fontWeight="600"
        >
          Ange verifieringskod
        </Typography>
      </Box>
      
      {(error || authError) && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            '& .MuiAlert-icon': { alignItems: 'center' }
          }}
        >
          {error || authError}
        </Alert>
      )}
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Vi har skickat en 6-siffrig verifieringskod till ditt mobilnummer.
      </Typography>
      
      <TextField
        label="Verifieringskod"
        type="text"
        fullWidth
        autoFocus
        margin="normal"
        required
        inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*' }}
        value={otpCode}
        onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
        autoComplete="one-time-code"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockOutlinedIcon color="action" fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />
      
      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        sx={{ 
          mt: 2, 
          mb: 3,
          height: 48
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Verifiera'}
      </Button>
    </Box>
  )
  
  // Check again here in case component re-renders while state update is in progress
  const showOtpForm = phoneVerificationInProgress;
  
  return showOtpForm ? renderOtpForm() : renderPhoneForm();
}

export default PhoneAuth 